// First-person arms: "FP arms" by DJMaesen (Sketchfab, CC-BY-4.0; scripts/import-fp-arms.mjs),
// brown leather fingerless gloves.
//
// Knife: the right hand makes a fist around the karambit, the left arm is folded away, and the arm
// is placed so the knife sits where the rig's knife rests, so the keyframed knife animations
// (knifeAnim.ts) play the same.
// Pistol: both arms follow the hands of the Glock rig (fpsRig.ts) with two-bone IK every frame, so
// its shoot and reload animations drive these arms; the rig's own low-poly arms are hidden.

import * as THREE from 'three';
import { animatedArm, type RigKnife, type RigPistol } from './fpsRig';
import {
  P_BEND,
  P_DX,
  P_DY,
  P_DZ,
  P_HOLD,
  P_PITCH,
  P_ROLL,
  P_SIDE,
  P_TWIST,
  P_YAW,
  POSE_SIZE,
} from './knifeAnim';

export const FP_ARMS_URL = `${import.meta.env.BASE_URL ?? '/'}assets/models/fp-arms/fp-arms.gltf`;

type Side = 'R' | 'L';
type Bones = Map<string, THREE.Bone>;

/** Bones by name without the exporter's numeric suffix ("point1R_035" -> "point1R"). */
function boneMap(scene: THREE.Object3D): Bones {
  const m: Bones = new Map();
  scene.traverse((o) => {
    if (o instanceof THREE.Bone) m.set(o.name.replace(/_\d+$/, ''), o);
  });
  return m;
}
function get(b: Bones, name: string): THREE.Bone {
  const o = b.get(name);
  if (!o) throw new Error('fp arms: no bone ' + name);
  return o;
}
const wpos = (o: THREE.Object3D) => o.getWorldPosition(new THREE.Vector3());

/** Rotate a bone about a world-space axis (through its own origin). */
function turnWorld(bone: THREE.Object3D, axis: THREE.Vector3, angle: number) {
  const parentQ = bone.parent!.getWorldQuaternion(new THREE.Quaternion());
  const worldQ = bone.getWorldQuaternion(new THREE.Quaternion());
  const q = new THREE.Quaternion().setFromAxisAngle(axis, angle).multiply(worldQ);
  bone.quaternion.copy(parentQ.invert().multiply(q));
  bone.updateMatrixWorld(true);
}

const FINGERS = ['point', 'middle', 'ring', 'pink'] as const;
type Finger = (typeof FINGERS)[number];

/** A relaxed hand's own directions, measured from its bones (world space). */
interface HandFrame {
  /** Index knuckle to pinky knuckle. */
  across: THREE.Vector3;
  /** Wrist toward the knuckles. */
  fwd: THREE.Vector3;
  /** Out of the palm. */
  palm: THREE.Vector3;
  /** Knuckle span (index to pinky), the hand's size. */
  span: number;
  knuckles: THREE.Vector3;
}

function handFrame(b: Bones, side: Side): HandFrame {
  const w = (n: string) => wpos(get(b, n + side));
  const index = w('point1');
  const pinky = w('pink1');
  const knuckles = index.clone().add(pinky).multiplyScalar(0.5);
  const across = pinky.clone().sub(index);
  const span = across.length();
  across.normalize();
  const fwd = knuckles.clone().sub(w('hand'));
  fwd.addScaledVector(across, -fwd.dot(across)).normalize();
  // The model's relaxed fingers bend a little toward the palm: that says which side it is.
  const bend = wpos(get(b, `middle3${side}_end`)).sub(w('middle1'));
  const palm = new THREE.Vector3().crossVectors(fwd, across);
  if (palm.dot(bend) < 0) palm.negate();
  return { across, fwd, palm, span, knuckles };
}

/** Distance from `p` to the line through `a` along unit `d`. */
function lineDist(p: THREE.Vector3, a: THREE.Vector3, d: THREE.Vector3) {
  const v = p.clone().sub(a);
  return v.addScaledVector(d, -v.dot(d)).length();
}

/**
 * Close fingers around a handle (a cylinder along `axisDir` through `axis`, radius `radius` at the
 * finger's centre line): each joint, base to tip, bends toward the palm until the next joint touches
 * the handle, the way a hand closes on something. `open` fingers only bend by the given angles.
 */
function wrapFingers(
  b: Bones,
  side: Side,
  h: HandFrame,
  axis: THREE.Vector3,
  axisDir: THREE.Vector3,
  radius: number,
  open: Partial<Record<Finger, readonly [number, number, number]>> = {},
) {
  for (const f of FINGERS) {
    const joints = [1, 2, 3].map((j) => get(b, `${f}${j}${side}`));
    const ends = [joints[1], joints[2], get(b, `${f}3${side}_end`)];
    // Each finger's own hinge: across its base direction and the palm normal.
    const dir = wpos(joints[1]).sub(wpos(joints[0])).normalize();
    const hinge = new THREE.Vector3().crossVectors(dir, h.palm).normalize();
    for (let j = 0; j < 3; j++) {
      const fixed = open[f];
      if (fixed) {
        turnWorld(joints[j], hinge, fixed[j]);
        continue;
      }
      // Close in small steps until the next joint reaches the handle (or the joint's limit).
      const limit = j === 0 ? 1.7 : j === 1 ? 1.9 : 1.4;
      for (let a = 0; a < limit; a += 0.03) {
        if (lineDist(wpos(ends[j]), axis, axisDir) <= radius) break;
        turnWorld(joints[j], hinge, 0.03);
      }
    }
  }
}

/** Bend the thumb so its tip goes to `target` (a few CCD passes over its joints). */
function reachThumb(b: Bones, side: Side, target: THREE.Vector3) {
  const joints = [get(b, 'thumb1' + side), get(b, 'thumb2' + side), get(b, 'thumb3' + side)];
  const end = get(b, `thumb3${side}_end`);
  const axis = new THREE.Vector3();
  for (let pass = 0; pass < 16; pass++) {
    for (let j = joints.length - 1; j >= 0; j--) {
      const o = wpos(joints[j]);
      const toEnd = wpos(end).sub(o).normalize();
      const toTarget = target.clone().sub(o).normalize();
      const angle = Math.acos(Math.min(1, Math.max(-1, toEnd.dot(toTarget))));
      if (angle < 1e-3) continue;
      axis.crossVectors(toEnd, toTarget).normalize();
      // Small steps keep the joints from folding the wrong way.
      turnWorld(joints[j], axis, Math.min(angle, 0.2));
    }
  }
}

/** Where a handle sits in a hand: across the palm, just inside the knuckles. */
function handleAxis(h: HandFrame, radius: number) {
  return h.knuckles
    .clone()
    .addScaledVector(h.palm, h.span * PALM_DEPTH + radius)
    .addScaledVector(h.fwd, -h.span * HANDLE_BACK);
}

/** Half the palm's thickness and how far the handle sits back from the knuckles, in knuckle spans. */
const PALM_DEPTH = 0.12;
const HANDLE_BACK = 0.12;
/** Finger half-thickness, in knuckle spans (the wrap stops when the finger's centre line is this far out). */
const FINGER_HALF = 0.11;

function toPhong(scene: THREE.Object3D) {
  scene.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    o.frustumCulled = false;
    const m = o.material as THREE.MeshStandardMaterial;
    o.material = new THREE.MeshPhongMaterial({
      map: m.map,
      normalMap: m.normalMap,
      specular: 0x222222,
      shininess: 18,
    });
  });
}

let loading: Promise<THREE.Object3D | null> | null = null;
function loadArms(): Promise<THREE.Object3D | null> {
  loading ??= (async () => {
    try {
      const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
      const gltf = await new GLTFLoader().loadAsync(FP_ARMS_URL);
      toPhong(gltf.scene);
      return gltf.scene;
    } catch {
      return null;
    }
  })();
  return loading;
}

async function freshArms(): Promise<THREE.Object3D | null> {
  const src = await loadArms();
  if (!src) return null;
  const { clone } = await import('three/addons/utils/SkeletonUtils.js');
  const scene = clone(src);
  scene.updateMatrixWorld(true);
  return scene;
}

/* ---------------- Knife ---------------- */

/**
 * Knife rest pose in camera space, like CS2: fist low on the right, palm down, the handle across the
 * fist (ring and index finger on the left, blade out past the pinky on the right) and the knuckles
 * up and forward, so the blade hooks upward.
 */
const KNIFE_FIST = new THREE.Vector3(0.07, -0.11, -0.5);
/** Index knuckle -> pinky knuckle. */
const KNIFE_ACROSS = new THREE.Vector3(1, -0.42, -0.35);
/** Wrist -> knuckles. */
const KNIFE_KNUCKLES = new THREE.Vector3(-0.15, 0.3, -0.95);
/** Arm size: camera units per metre of the arm model (about life size). */
const ARM_SCALE = 1.15;
/** Handle radius in knuckle spans. */
const KNIFE_HANDLE = 0.17;

/** Ring centre and where the blade starts (along the handle) of a karambit, in its own coordinates. */
/**
 * How a knife without a ring is held (set on the model's userData.grip by its loader): where the
 * index finger goes and how much handle the fist covers, in the knife's coordinates; optionally its
 * own inspect pose (fist placement like INSPECT_FIST and friends), held in the closed fist instead of
 * on the open palm.
 */
export interface KnifeGrip {
  index: THREE.Vector3;
  handle: number;
  inspect?: {
    fist: THREE.Vector3;
    across: THREE.Vector3;
    knuckles: THREE.Vector3;
    palm: boolean;
    /** Turn the knife end over end in the closed fist (a reverse grip: pommel up out of the fist). */
    flip?: boolean;
    /** Turn it this much round its length in the fist (radians), to show the flat. */
    twist?: number;
    /** Then this turn round the middle of the grip (the knife's own axes), to aim it. */
    turn?: { axis: THREE.Vector3; angle: number };
    /** How much of the inspect's wrist turns (knifeAnim.ts) to play, and which way (default 1). */
    wrist?: number;
  };
  /** A turn of the knife in the fist only (its own coordinates; the palm grip is left alone). */
  restTurn?: THREE.Matrix4;
}

function measureKnife(knife: THREE.Object3D) {
  // Knives held without a ring (the butterfly) say where the index finger goes and how much handle
  // the fist covers.
  const grip = knife.userData.grip as KnifeGrip | undefined;
  if (grip) return { ringCentre: grip.index.clone(), handle: grip.handle };
  knife.updateMatrixWorld(true);
  const ring = knife.getObjectByName('ring');
  const blade = knife.getObjectByName('blade');
  const ringCentre = ring
    ? new THREE.Box3().setFromObject(ring).getCenter(new THREE.Vector3())
    : new THREE.Vector3(-0.62, 0, 0);
  const bladeStart = blade ? new THREE.Box3().setFromObject(blade).min.x : 0.47;
  return { ringCentre, handle: bladeStart - ringCentre.x };
}
/** Ring-to-blade length of the handle, in knuckle spans (the fist plus a little). */
const KNIFE_HANDLE_LENGTH = 1.45;
/** Inspect palm grip: handle from the index knuckle toward the heel of the hand, leaning this much
 *  toward the pinky side; which way the hook faces in the palm (+1/-1). */
const PALM_HANDLE_TILT = 1.2;
const PALM_HOOK = 1;
/** How much the blade's hook turns toward the back of the hand (vs. toward the knuckles). */
const KNIFE_HOOK_UP = 1.2;

/** Build the knife arm (right hand only), resting as described above. */
export async function loadFpArmsKnife(karambit: THREE.Object3D): Promise<RigKnife | null> {
  try {
    const scene = await freshArms();
    if (!scene) return null;
    const b = boneMap(scene);
    get(b, 'armL').scale.setScalar(1e-4);
    scene.updateMatrixWorld(true);
    const h = handFrame(b, 'R');
    const r = KNIFE_HANDLE * h.span;
    const axis = handleAxis(h, r);
    const m = measureKnife(karambit);
    const s = (KNIFE_HANDLE_LENGTH * h.span) / m.handle;
    const hand = get(b, 'handR');
    // Finger bones whose pose changes between the fist (idle) and the open palm (inspect).
    const fingerBones: THREE.Bone[] = [];
    for (const f of [...FINGERS, 'thumb']) for (let j = 1; j <= 3; j++) fingerBones.push(get(b, `${f}${j}R`));
    const relaxed = fingerBones.map((o) => o.quaternion.clone());

    // Inspect grip, like CS2: the hand open with the fingers up, the index finger still through the
    // ring, the knife lying flat on the palm with the handle running down toward the heel of the
    // hand and the blade out at the bottom.
    wrapFingers(b, 'R', h, axis, h.across, 0, {
      point: [0.35, 0.45, 0.3],
      middle: [-0.25, -0.15, 0],
      ring: [-0.2, -0.15, 0],
      pink: [-0.15, -0.15, 0],
    });
    scene.updateMatrixWorld(true);
    const openQ = fingerBones.map((o) => o.quaternion.clone());
    const palmKnife = new THREE.Matrix4();
    {
      const px = h.fwd.clone().negate().addScaledVector(h.across, PALM_HANDLE_TILT).normalize();
      // The knife lies flat on the palm (its broad side facing out of the palm, so toward the camera
      // when the palm faces it), the hook curling across the palm.
      const pzz = h.palm.clone();
      pzz.addScaledVector(px, -pzz.dot(px)).normalize();
      const py = new THREE.Vector3().crossVectors(pzz, px).multiplyScalar(PALM_HOOK);
      if (PALM_HOOK < 0) pzz.negate();
      const ring = wpos(get(b, 'point1R')).addScaledVector(h.palm, h.span * (PALM_DEPTH + 0.3));
      const origin = ring
        .clone()
        .addScaledVector(px, -m.ringCentre.x * s)
        .addScaledVector(py, -m.ringCentre.y * s);
      const world = new THREE.Matrix4().compose(
        origin,
        new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(px, py, pzz)),
        new THREE.Vector3(s, s, s),
      );
      hand.updateMatrixWorld(true);
      palmKnife.copy(hand.matrixWorld).invert().multiply(world);
    }
    fingerBones.forEach((o, i) => o.quaternion.copy(relaxed[i]));
    scene.updateMatrixWorld(true);

    // Idle grip: a fist round the handle.
    wrapFingers(b, 'R', h, axis, h.across, r + FINGER_HALF * h.span);

    // Knife, held like CS2's karambit: index finger through the ring, the handle across the fist and
    // the blade out past the pinky, hooking toward the back of the hand.
    const x = h.across.clone();
    // Hook between the back of the hand and the knuckles: the blade runs forward and curls up.
    const y = h.palm.clone().multiplyScalar(KNIFE_HOOK_UP).addScaledVector(h.fwd, -1);
    y.addScaledVector(x, -y.dot(x)).normalize();
    const z = new THREE.Vector3().crossVectors(x, y);
    const indexAlong = wpos(get(b, 'point1R')).sub(axis).dot(x);
    const origin = axis.clone().addScaledVector(x, indexAlong - m.ringCentre.x * s);
    const knifeFrame = new THREE.Matrix4().compose(
      origin,
      new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z)),
      new THREE.Vector3(s, s, s),
    );
    hand.updateMatrixWorld(true);
    const knife = karambit.clone();
    hand.matrixWorld
      .clone()
      .invert()
      .multiply(knifeFrame)
      .decompose(knife.position, knife.quaternion, knife.scale);
    const restTurn = (karambit.userData.grip as KnifeGrip | undefined)?.restTurn;
    if (restTurn)
      new THREE.Matrix4()
        .compose(knife.position, knife.quaternion, knife.scale)
        .multiply(restTurn)
        .decompose(knife.position, knife.quaternion, knife.scale);
    hand.add(knife);
    // Thumb over the index finger's middle joint, on its outside.
    const p2 = wpos(get(b, 'point2R'));
    const indexAxis = axis.clone().addScaledVector(x, indexAlong);
    reachThumb(
      b,
      'R',
      p2.clone().add(
        p2
          .clone()
          .sub(indexAxis)
          .normalize()
          .multiplyScalar(FINGER_HALF * h.span * 1.6),
      ),
    );
    scene.updateMatrixWorld(true);
    // The two grips, blended by P_HOLD: fingers and the knife's place in the hand.
    const fistQ = fingerBones.map((o) => o.quaternion.clone());
    const fistPos = knife.position.clone();
    const fistRot = knife.quaternion.clone();
    const palmPos = new THREE.Vector3();
    const palmRot = new THREE.Quaternion();
    palmKnife.decompose(palmPos, palmRot, new THREE.Vector3());
    const grip = karambit.userData.grip as KnifeGrip | undefined;
    const ins = grip?.inspect;
    const flip = !!ins?.flip;
    // Knives that turn in the closed fist for the inspect (flip, twist or an aim) keep their fingers.
    const inFist = !!ins && !ins.palm && (flip || !!ins.twist || !!ins.turn);
    if (inFist) {
      // The inspect's 'palm' grip is the same fist with the knife turned round the middle of the grip:
      // end over end (about its flat side's normal) for a flip, round its length for a twist.
      const c = new THREE.Vector3(m.ringCentre.x + m.handle / 2, m.ringCentre.y, m.ringCentre.z);
      new THREE.Matrix4()
        .compose(fistPos, fistRot, knife.scale)
        .multiply(new THREE.Matrix4().makeTranslation(c.x, c.y, c.z))
        .multiply(new THREE.Matrix4().makeRotationZ(flip ? Math.PI : 0))
        .multiply(new THREE.Matrix4().makeRotationX(grip?.inspect?.twist ?? 0))
        .multiply(
          grip?.inspect?.turn
            ? new THREE.Matrix4().makeRotationAxis(grip.inspect.turn.axis, grip.inspect.turn.angle)
            : new THREE.Matrix4(),
        )
        .multiply(new THREE.Matrix4().makeTranslation(-c.x, -c.y, -c.z))
        .decompose(palmPos, palmRot, new THREE.Vector3());
    }
    let gripW = 0;
    const setGrip = (w: number) => {
      if (w === gripW) return;
      gripW = w;
      if (!inFist) fingerBones.forEach((o, i) => o.quaternion.slerpQuaternions(fistQ[i], openQ[i], w));
      knife.position.lerpVectors(fistPos, palmPos, w);
      knife.quaternion.slerpQuaternions(fistRot, palmRot, w);
    };

    // The arm placed for a hand pose (fist position, index->pinky, wrist->knuckles): at rest, and
    // raised for the inspect; animations blend between the two (P_HOLD).
    const handQ = new THREE.Quaternion()
      .setFromRotationMatrix(
        new THREE.Matrix4().makeBasis(h.across, h.fwd, new THREE.Vector3().crossVectors(h.across, h.fwd)),
      )
      .invert();
    const k = ARM_SCALE;
    const placed = (fist: THREE.Vector3, across: THREE.Vector3, knuckles: THREE.Vector3) => {
      const ax = across.clone().normalize();
      const kn = knuckles.clone();
      kn.addScaledVector(ax, -kn.dot(ax)).normalize();
      const q = new THREE.Quaternion()
        .setFromRotationMatrix(
          new THREE.Matrix4().makeBasis(ax, kn, new THREE.Vector3().crossVectors(ax, kn)),
        )
        .multiply(handQ);
      const p = fist.clone().sub(axis.clone().multiplyScalar(k).applyQuaternion(q));
      return { q, p };
    };
    const rest = placed(KNIFE_FIST, KNIFE_ACROSS, KNIFE_KNUCKLES);
    const own = (karambit.userData.grip as KnifeGrip | undefined)?.inspect;
    const held = own
      ? placed(own.fist, own.across, own.knuckles)
      : placed(INSPECT_FIST, INSPECT_ACROSS, INSPECT_KNUCKLES);
    const palmInspect = own?.palm ?? true;
    const arm = new THREE.Group();
    arm.add(scene);
    arm.quaternion.copy(rest.q);
    arm.position.copy(rest.p);
    arm.scale.setScalar(k);
    const names = { shoulder: b.get('armR')!.name, elbow: b.get('forearmR')!.name, wrist: hand.name };
    // The inspect bends the arm (two-bone IK) instead of moving it: the arm stays where it rests, and
    // the shoulder and elbow turn so the hand reaches the inspect's place and turn. (Moving the whole
    // arm to the hand pose swung the forearm across the view.)
    const reach = twoBoneReach(arm, get(b, 'armR'), get(b, 'elbowR'), hand, rest, held, k);
    let hold = 0;
    const animated = animatedArm(arm, scene, names, () => reach(hold));
    // The free left hand rests open at the lower left (it doesn't swing with the knife arm); it drops
    // out of view while the knife is held up for the inspect.
    const left = await restingLeftHand();
    if (left) animated.object.add(left);
    const leftPos = left?.position.clone() ?? new THREE.Vector3();
    const leftRot = left?.quaternion.clone() ?? new THREE.Quaternion();
    const leftTurn = new THREE.Euler();
    const leftQ = new THREE.Quaternion();
    const wristGain = own?.wrist ?? 1;
    const scaled = new Array<number>(POSE_SIZE).fill(0);
    return {
      object: animated.object,
      knife,
      pose(p: readonly number[], time = 0) {
        const w = p[P_HOLD] ?? 0;
        hold = w;
        if (left) {
          // The free hand breathes a little and reacts to the knife arm: it pulls back and braces
          // against slashes and stabs, and drops out of view for the inspect.
          const sway = Math.sin(time * 1.7);
          left.position.set(
            leftPos.x - 0.25 * p[P_DX] + 0.003 * Math.sin(time * 1.1),
            leftPos.y - 0.35 * w + 0.005 * sway - 0.2 * Math.abs(p[P_DY]) - 0.03 * Math.abs(p[P_YAW]),
            leftPos.z + 0.12 * Math.abs(p[P_DZ]) + 0.04 * Math.abs(p[P_YAW]),
          );
          leftTurn.set(
            0.04 * sway - 0.25 * p[P_PITCH],
            0.3 * p[P_YAW],
            0.03 * Math.sin(time * 1.3) - 0.3 * p[P_ROLL],
          );
          left.quaternion.copy(leftRot).multiply(leftQ.setFromEuler(leftTurn));
        }
        // The hand opens into the palm grip in the second half of the raise.
        setGrip(palmInspect || inFist ? Math.min(1, Math.max(0, (w - 0.35) / 0.5)) : 0);
        if (wristGain === 1) {
          animated.pose(p);
          return;
        }
        // This knife's own share of the wrist turns while it's held up.
        const g = 1 + (wristGain - 1) * w;
        for (let i = 0; i < POSE_SIZE; i++) scaled[i] = p[i] ?? 0;
        scaled[P_TWIST] *= g;
        scaled[P_BEND] *= g;
        scaled[P_SIDE] *= g;
        animated.pose(scaled);
      },
    };
  } catch {
    return null;
  }
}

/**
 * Two-bone IK for the knife arm: returns a function that, for a blend w (0 = rest, 1 = the inspect),
 * turns the upper arm and forearm so the hand reaches the inspect's hand placement and gives the hand
 * its turn, with the elbow bending on the side it bends at rest. True if it bent the arm (w > 0).
 * `rest` and `held` are arm placements (placed() in loadFpArmsKnife); the arm itself stays at rest.
 */
/** How much of the arm's full length the inspect reaches with before the shoulder moves (a bent elbow). */
const REACH = 0.93;

function twoBoneReach(
  arm: THREE.Object3D,
  upper: THREE.Bone,
  fore: THREE.Bone,
  hand: THREE.Bone,
  rest: { q: THREE.Quaternion; p: THREE.Vector3 },
  held: { q: THREE.Quaternion; p: THREE.Vector3 },
  scale: number,
): (w: number) => boolean {
  const sc = new THREE.Vector3(scale, scale, scale);
  const restM = new THREE.Matrix4().compose(rest.p, rest.q, sc);
  const heldM = new THREE.Matrix4().compose(held.p, held.q, sc);
  // The hand in the arm's own space: at rest, and where the inspect placement would put it.
  arm.updateMatrixWorld(true);
  const handInArm = new THREE.Matrix4().copy(arm.matrixWorld).invert().multiply(hand.matrixWorld);
  const heldInArm = restM.clone().invert().multiply(heldM).multiply(handInArm);
  const p0 = new THREE.Vector3();
  const q0 = new THREE.Quaternion();
  const p1 = new THREE.Vector3();
  const q1 = new THREE.Quaternion();
  const unit = new THREE.Vector3();
  handInArm.decompose(p0, q0, unit);
  heldInArm.decompose(p1, q1, unit);
  const restQ = [upper.quaternion.clone(), fore.quaternion.clone(), hand.quaternion.clone()];
  const restShoulder = upper.position.clone();
  const shifted = new THREE.Vector3();
  // Scratch (no allocations per frame).
  const tp = new THREE.Vector3();
  const tq = new THREE.Quaternion();
  const tm = new THREE.Matrix4();
  const one = new THREE.Vector3(1, 1, 1);
  const S = new THREE.Vector3();
  const E = new THREE.Vector3();
  const W = new THREE.Vector3();
  const T = new THREE.Vector3();
  const dir = new THREE.Vector3();
  const pole = new THREE.Vector3();
  const goal = new THREE.Vector3();
  const from = new THREE.Vector3();
  const to = new THREE.Vector3();
  const qr = new THREE.Quaternion();
  const qb = new THREE.Quaternion();
  const qp = new THREE.Quaternion();
  const ts = new THREE.Vector3();
  const turn = (bone: THREE.Object3D, a: THREE.Vector3, b2: THREE.Vector3) => {
    qr.setFromUnitVectors(a, b2);
    bone.getWorldQuaternion(qb);
    bone.parent!.getWorldQuaternion(qp).invert();
    bone.quaternion.copy(qp).multiply(qr).multiply(qb);
    bone.updateMatrixWorld(true);
  };
  let bent = true;
  return (w: number) => {
    if (w <= 0) {
      if (bent) {
        upper.position.copy(restShoulder);
        upper.quaternion.copy(restQ[0]);
        fore.quaternion.copy(restQ[1]);
        hand.quaternion.copy(restQ[2]);
        upper.updateMatrixWorld(true);
        bent = false;
      }
      return false;
    }
    bent = true;
    upper.position.copy(restShoulder);
    upper.quaternion.copy(restQ[0]);
    fore.quaternion.copy(restQ[1]);
    hand.quaternion.copy(restQ[2]);
    upper.updateMatrixWorld(true);
    // The target hand, in the world (the arm's swing and the view move it along).
    tp.lerpVectors(p0, p1, w);
    tq.slerpQuaternions(q0, q1, w);
    tm.compose(tp, tq, one).premultiply(arm.matrixWorld);
    tm.decompose(T, tq, ts);
    upper.getWorldPosition(S);
    fore.getWorldPosition(E);
    hand.getWorldPosition(W);
    const a = S.distanceTo(E);
    const b2 = E.distanceTo(W);
    // The arm rests nearly straight: when the hand has to go further, the shoulder (off screen)
    // slides toward it, so the elbow keeps a little bend and nothing swings across the view.
    const reachMax = (a + b2) * (1 - (1 - REACH) * w);
    dir.subVectors(T, S);
    const far = dir.length() - reachMax;
    if (far > 0) {
      shifted.copy(S).addScaledVector(dir.normalize(), far);
      upper.position.copy(upper.parent!.worldToLocal(shifted));
      upper.updateMatrixWorld(true);
      upper.getWorldPosition(S);
      fore.getWorldPosition(E);
      hand.getWorldPosition(W);
      dir.subVectors(T, S);
    }
    const d = Math.min(a + b2 - 1e-6, Math.max(Math.abs(a - b2) + 1e-6, dir.length()));
    dir.normalize();
    // Elbow: on the plane through the shoulder, the goal and the resting elbow.
    const x = (a * a - b2 * b2 + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, a * a - x * x));
    pole.subVectors(E, S);
    pole.addScaledVector(dir, -pole.dot(dir));
    if (pole.lengthSq() < 1e-12) pole.set(0, -1, 0);
    pole.normalize();
    goal.copy(S).addScaledVector(dir, x).addScaledVector(pole, h);
    turn(upper, from.subVectors(E, S).normalize(), to.subVectors(goal, S).normalize());
    fore.getWorldPosition(E);
    hand.getWorldPosition(W);
    goal.copy(S).addScaledVector(dir, d);
    turn(fore, from.subVectors(W, E).normalize(), to.subVectors(goal, E).normalize());
    // The hand's turn.
    hand.parent!.getWorldQuaternion(qp).invert();
    hand.quaternion.copy(qp).multiply(tq);
    hand.updateMatrixWorld(true);
    return true;
  };
}

/**
 * Inspect pose, like CS2: the hand up at centre-right with the palm to the camera and the fingers
 * curled over the handle, the ring at the top by the index finger and the blade hanging out at the
 * bottom left.
 */
const INSPECT_FIST = new THREE.Vector3(0.07, 0.0, -0.45);
const INSPECT_ACROSS = new THREE.Vector3(-1, -0.1, 0.05);
const INSPECT_KNUCKLES = new THREE.Vector3(0.1, 1, -0.1);

/** Left hand resting open at the lower left while the right hand holds the knife. */
const LEFT_REST = new THREE.Vector3(-0.15, -0.15, -0.5);
/** Its index knuckle -> pinky knuckle, and wrist -> knuckles (palm down, fingers forward). */
const LEFT_ACROSS = new THREE.Vector3(-1, -0.25, 0.15);
const LEFT_KNUCKLES = new THREE.Vector3(0.3, 0.35, -0.9);

async function restingLeftHand(): Promise<THREE.Object3D | null> {
  const scene = await freshArms();
  if (!scene) return null;
  const b = boneMap(scene);
  get(b, 'armR').scale.setScalar(1e-4);
  scene.updateMatrixWorld(true);
  const h = handFrame(b, 'L');
  // Relaxed: fingers a little bent, thumb loose.
  wrapFingers(b, 'L', h, h.knuckles, h.across, 0, {
    point: [0.25, 0.3, 0.15],
    middle: [0.3, 0.35, 0.15],
    ring: [0.35, 0.35, 0.15],
    pink: [0.4, 0.35, 0.15],
  });
  scene.updateMatrixWorld(true);
  const ax = LEFT_ACROSS.clone().normalize();
  const kn = LEFT_KNUCKLES.clone();
  kn.addScaledVector(ax, -kn.dot(ax)).normalize();
  const restQ = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(ax, kn, new THREE.Vector3().crossVectors(ax, kn)),
  );
  const handQ = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(h.across, h.fwd, new THREE.Vector3().crossVectors(h.across, h.fwd)),
  );
  const g = new THREE.Group();
  g.add(scene);
  g.quaternion.copy(restQ).multiply(handQ.invert());
  g.scale.setScalar(ARM_SCALE);
  g.position.copy(LEFT_REST).sub(h.knuckles.clone().multiplyScalar(ARM_SCALE).applyQuaternion(g.quaternion));
  return g;
}

/* ---------------- Pistol ---------------- */

/** Pistol grip radius in knuckle spans (a bit thicker than the knife handle). */
const PISTOL_GRIP = 0.2;
/** How far up the grip the hand sits (from the magazine's middle), in knuckle spans. */
const PISTOL_HIGH = 0.3;

/** One arm solved toward a target hand pose each frame. */
class IkArm {
  private readonly upper: THREE.Bone;
  private readonly lower: THREE.Bone;
  private readonly hand: THREE.Bone;
  private readonly rest: [THREE.Bone, THREE.Quaternion][] = [];
  private readonly a: number;
  private readonly bLen: number;
  /** Elbow hint, in the arms' root space: out to the side and down. */
  private readonly pole: THREE.Vector3;
  private readonly v = Array.from({ length: 6 }, () => new THREE.Vector3());
  private readonly origin = new THREE.Vector3();
  private readonly want = new THREE.Vector3();
  private readonly q = Array.from({ length: 4 }, () => new THREE.Quaternion());

  constructor(
    b: Bones,
    side: Side,
    private readonly root: THREE.Object3D,
  ) {
    this.upper = get(b, 'arm' + side);
    this.lower = get(b, 'elbow' + side);
    this.hand = get(b, 'hand' + side);
    for (const bone of [this.upper, this.lower, get(b, 'forearm' + side), this.hand])
      this.rest.push([bone, bone.quaternion.clone()]);
    root.updateMatrixWorld(true);
    const s = wpos(this.upper);
    const e = wpos(this.lower);
    const w = wpos(this.hand);
    this.a = s.distanceTo(e);
    this.bLen = e.distanceTo(w);
    this.pole = new THREE.Vector3(side === 'R' ? -1 : 1, -1.2, 0).normalize();
  }

  /** Put the hand at `target` (world) with world rotation `targetQ`. */
  solve(target: THREE.Vector3, targetQ: THREE.Quaternion) {
    const [s, e, w, d, pole, tmp] = this.v;
    const [q0, q1, q2, q3] = this.q;
    for (const [bone, q] of this.rest) bone.quaternion.copy(q);
    this.upper.updateMatrixWorld(true);
    s.setFromMatrixPosition(this.upper.matrixWorld);
    // Reach: clamp to just under full length.
    d.copy(target).sub(s);
    const dist = Math.min(d.length(), (this.a + this.bLen) * 0.999);
    d.normalize();
    // Elbow: law of cosines, bent toward the pole.
    const cosA = (this.a * this.a + dist * dist - this.bLen * this.bLen) / (2 * this.a * dist);
    const angA = Math.acos(Math.min(1, Math.max(-1, cosA)));
    pole.copy(this.pole).transformDirection(this.root.matrixWorld);
    pole.addScaledVector(d, -pole.dot(d)).normalize();
    e.copy(s)
      .addScaledVector(d, Math.cos(angA) * this.a)
      .addScaledVector(pole, Math.sin(angA) * this.a);
    // Aim the upper arm at the elbow, then the forearm at the wrist.
    this.aim(this.upper, this.lower, e, q0, q1, tmp);
    w.copy(s).addScaledVector(d, dist);
    this.aim(this.lower, this.hand, w, q2, q3, tmp);
    // Hand rotation in world space.
    this.hand.parent!.getWorldQuaternion(q0);
    this.hand.quaternion.copy(q0.invert().multiply(targetQ));
    this.hand.updateMatrixWorld(true);
  }

  /** Turn `bone` so its child `next` lies on the line toward `point`. */
  private aim(
    bone: THREE.Bone,
    next: THREE.Object3D,
    point: THREE.Vector3,
    qa: THREE.Quaternion,
    qb: THREE.Quaternion,
    tmp: THREE.Vector3,
  ) {
    bone.updateMatrixWorld(true);
    const o = this.origin.setFromMatrixPosition(bone.matrixWorld);
    tmp.setFromMatrixPosition(next.matrixWorld).sub(o).normalize();
    this.want.copy(point).sub(o).normalize();
    qa.setFromUnitVectors(tmp, this.want);
    bone.getWorldQuaternion(qb);
    qa.multiply(qb);
    bone.parent!.getWorldQuaternion(qb);
    bone.quaternion.copy(qb.invert().multiply(qa));
    bone.updateMatrixWorld(true);
  }
}

/**
 * Swap the Glock rig's arms for these: hides its arm mesh and returns a per-frame update (call it
 * after the rig's animation mixer) that puts these hands where the rig's hands are.
 */
export async function attachFpArmsToPistol(
  p: RigPistol,
  rigScene: THREE.Object3D,
): Promise<{ follow: () => void; showSupport: (on: boolean) => void } | null> {
  try {
    const scene = await freshArms();
    if (!scene) return null;
    const b = boneMap(scene);
    // Shooting hand: wrapped round the grip, index straight along the frame, thumb along the left
    // side pointing at the muzzle. Support hand: loosely closed (it only shows while reloading).
    const hR = handFrame(b, 'R');
    const rGrip = PISTOL_GRIP * hR.span;
    const axisR = handleAxis(hR, rGrip);
    wrapFingers(b, 'R', hR, axisR, hR.across, rGrip + FINGER_HALF * hR.span, { point: [0.1, 0.25, 0.1] });
    reachThumb(
      b,
      'R',
      wpos(get(b, 'middle3R_end'))
        .addScaledVector(hR.fwd, 0.5 * hR.span)
        .addScaledVector(hR.across, -0.3 * hR.span),
    );
    const hL = handFrame(b, 'L');
    wrapFingers(b, 'L', hL, hL.knuckles, hL.across, 0, {
      point: [0.7, 0.6, 0.3],
      middle: [0.75, 0.6, 0.3],
      ring: [0.8, 0.6, 0.3],
      pink: [0.85, 0.6, 0.3],
    });
    scene.updateMatrixWorld(true);

    const rig = (n: string) => {
      const o = rigScene.getObjectByName(n);
      if (!o) throw new Error('fps rig: no ' + n);
      return o;
    };
    const holder = p.object;
    holder.updateMatrixWorld(true);
    const inHolder = (o: THREE.Object3D) => holder.worldToLocal(wpos(o));
    // Size: forearm length (elbow to wrist; bone lengths don't change with the pose) matches the rig's.
    const rigForearm = inHolder(rig('LowerArmR001')).distanceTo(inHolder(rig('HandR001')));
    const ourForearm = wpos(get(b, 'elbowR')).distanceTo(wpos(get(b, 'handR')));
    const arms = new THREE.Group();
    arms.add(scene);
    arms.scale.setScalar(rigForearm / ourForearm);
    // Shoulders where the rig's shoulders are (their midpoint), facing the same way as the camera.
    const mid = inHolder(rig('UpperArmR001'))
      .add(inHolder(rig('UpperArmL')))
      .multiplyScalar(0.5);
    arms.rotation.y = Math.PI;
    arms.updateMatrix();
    // (wpos already includes the arms group's rotation and scale.)
    const ourMid = wpos(get(b, 'armR'))
      .add(wpos(get(b, 'armL')))
      .multiplyScalar(0.5);
    arms.position.copy(mid).sub(ourMid);
    holder.add(arms);
    holder.updateMatrixWorld(true);

    // Offsets from the rig's hand bones to ours, from the rest pose: our right fist's grip frame on
    // the rig's gun grip (magazine axis), our left hand where the rig's left hand is.
    const gripRig = new THREE.Matrix4();
    {
      const w = (n: string) => wpos(rig(n));
      const up = w('Magazine_end').sub(w('Magazine')).normalize();
      const fwd = w('Barrel_end').sub(w('Barrel')).normalize();
      fwd.addScaledVector(up, -fwd.dot(up)).normalize();
      // Our grip frame: x = index -> pinky (down the grip), fwd along the forearm (toward the muzzle).
      const x = up.clone().negate();
      const z = new THREE.Vector3().crossVectors(x, fwd);
      // High on the grip: the web of the hand right under the slide.
      gripRig.compose(
        w('Magazine').addScaledVector(up, PISTOL_HIGH * hR.span * arms.scale.x),
        new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, fwd, z)),
        new THREE.Vector3(1, 1, 1),
      );
    }
    // Our grip frame: the handle axis in the fist, across (down the grip) and the knuckle direction
    // (measured before the arms were placed, so carried into where they are now).
    const zR = new THREE.Vector3().crossVectors(hR.across, hR.fwd);
    const gripOurs = scene.matrixWorld
      .clone()
      .multiply(
        new THREE.Matrix4().compose(
          axisR,
          new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(hR.across, hR.fwd, zR)),
          new THREE.Vector3(1, 1, 1),
        ),
      );
    const rigHandR = rig('HandR001');
    const rigHandL = rig('HandL');
    const ourHandR = get(b, 'handR');
    const ourHandL = get(b, 'handL');
    // Our right hand's world matrix that puts our grip frame on the rig's: hand = gripRig * gripOurs^-1 * hand.
    const handR0 = gripRig.clone().multiply(gripOurs.clone().invert()).multiply(ourHandR.matrixWorld);
    const offR = rigHandR.matrixWorld.clone().invert().multiply(handR0);
    // Left: keep our hand's own orientation relative to the right hand's, placed at the rig's left hand.
    const handL0 = handR0
      .clone()
      .multiply(ourHandR.matrixWorld.clone().invert())
      .multiply(ourHandL.matrixWorld);
    handL0.setPosition(wpos(rigHandL));
    const offL = rigHandL.matrixWorld.clone().invert().multiply(handL0);
    const offRpos = new THREE.Vector3();
    const offRq = new THREE.Quaternion();
    const offLpos = new THREE.Vector3();
    const offLq = new THREE.Quaternion();
    offR.decompose(offRpos, offRq, new THREE.Vector3());
    offL.decompose(offLpos, offLq, new THREE.Vector3());

    const ikR = new IkArm(b, 'R', arms);
    const ikL = new IkArm(b, 'L', arms);
    // The rig's own arms are replaced.
    const armMesh = rigScene.getObjectByName('ArmModel');
    if (armMesh) armMesh.visible = false;

    const m = new THREE.Matrix4();
    const tp = new THREE.Vector3();
    const tq = new THREE.Quaternion();
    const ts = new THREE.Vector3();
    const follow = (rigHand: THREE.Object3D, ik: IkArm, offPos: THREE.Vector3, offQ: THREE.Quaternion) => {
      m.compose(offPos, offQ, ts.set(1, 1, 1)).premultiply(rigHand.matrixWorld);
      m.decompose(tp, tq, ts);
      ik.solve(tp, tq);
    };
    const upperL = get(b, 'armL');
    let support = false;
    upperL.scale.setScalar(1e-4);
    return {
      follow: () => {
        holder.updateMatrixWorld(true);
        follow(rigHandR, ikR, offRpos, offRq);
        if (support) follow(rigHandL, ikL, offLpos, offLq);
      },
      showSupport: (on: boolean) => {
        if (on === support) return;
        support = on;
        upperL.scale.setScalar(on ? 1 : 1e-4);
      },
    };
  } catch {
    return null;
  }
}
